import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { followersApi } from '../../api/followers.api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../components/ui/Toast';
import Spinner from '../../components/ui/Spinner';
import { Bell, Check, Icon, Mail } from '../../components/ui/Icon';

/**
 * ADR-0044: follow the trip through its link. With an account, one tap (push
 * in the app). Without, an email address and a confirmation from the inbox.
 */
export default function FollowBox({ shareToken }: { shareToken: string }) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore(s => s.user);
  const [following, setFollowing] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!user) return;
    followersApi.status(shareToken).then(setFollowing).catch(() => setFollowing(false));
  }, [user, shareToken]);

  async function toggleFollow() {
    setBusy(true);
    try {
      if (following) await followersApi.unfollow(shareToken);
      else await followersApi.follow(shareToken);
      setFollowing(!following);
      toast(t(following ? 'shared.follow.unfollowed' : 'shared.follow.followed'), 'success');
    } catch {
      toast(t('shared.follow.error'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function subscribe(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await followersApi.subscribeByEmail(shareToken, email.trim(), i18n.language);
      setSent(true);
    } catch {
      toast(t('shared.follow.error'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card p-5 space-y-3">
      <div>
        <h2 className="font-semibold text-sm text-slate-800 dark:text-slate-100 flex items-center gap-1.5">
          <Icon as={Bell} size={14} className="text-teal-600" />
          {t('shared.follow.title')}
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t(user ? 'shared.follow.hintAccount' : 'shared.follow.hintEmail')}</p>
      </div>

      {user ? (
        <button onClick={toggleFollow} disabled={busy || following === null} className={`${following ? 'btn-secondary' : 'btn-primary'} text-sm gap-1.5`}>
          {busy ? <Spinner size="sm" /> : following && <Icon as={Check} size={14} />}
          {t(following ? 'shared.follow.following' : 'shared.follow.follow')}
        </button>
      ) : sent ? (
        <p className="flex items-center gap-1.5 text-sm text-teal-700 dark:text-teal-300">
          <Icon as={Mail} size={14} />
          {t('shared.follow.checkInbox')}
        </p>
      ) : (
        <form onSubmit={subscribe} className="flex gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder={t('shared.follow.emailPlaceholder')}
            aria-label={t('shared.follow.emailPlaceholder')}
            className="input flex-1 min-w-0 text-sm"
          />
          <button type="submit" disabled={busy} className="btn-primary text-sm shrink-0">
            {busy ? <Spinner size="sm" /> : t('shared.follow.subscribe')}
          </button>
        </form>
      )}
    </section>
  );
}
